import { useEffect, useRef } from 'react';

/**
 * A counter-based scroll lock manager that handles nested modals/drawers correctly.
 * Each component that calls lock() should call unlock() when done.
 * Body scroll is only enabled when all locks are released.
 */
class ScrollLockManager {
    private lockCount = 0;
    // Inline styles captured at the 0→1 transition so we restore exactly what
    // was there before *any* lock — never another locker's "hidden".
    private original = { bodyOverflow: '', htmlOverflow: '', touchAction: '' };

    lock() {
        if (this.lockCount === 0) {
            const { body, documentElement: html } = document;
            this.original = {
                bodyOverflow: body.style.overflow,
                htmlOverflow: html.style.overflow,
                touchAction: body.style.touchAction,
            };
            body.style.overflow = 'hidden';
            html.style.overflow = 'hidden';
            body.style.touchAction = 'none';
        }
        this.lockCount++;
    }

    unlock() {
        this.lockCount--;
        if (this.lockCount <= 0) {
            this.lockCount = 0;
            const { body, documentElement: html } = document;
            body.style.overflow = this.original.bodyOverflow;
            html.style.overflow = this.original.htmlOverflow;
            body.style.touchAction = this.original.touchAction;
        }
    }

    forceUnlock() {
        this.lockCount = 0;
        const { body, documentElement: html } = document;
        body.style.overflow = '';
        html.style.overflow = '';
        body.style.touchAction = '';
    }
}

// Singleton instance
const scrollLockManager = new ScrollLockManager();

/**
 * Hook to lock body scroll when a modal/drawer is open.
 * Properly handles:
 * - Multiple nested modals
 * - Cleanup on unmount
 * - Fast navigation
 * 
 * @param isOpen - Whether the modal/drawer is open
 */
export function useScrollLock(isOpen: boolean) {
    const isLocked = useRef(false);

    useEffect(() => {
        if (isOpen && !isLocked.current) {
            scrollLockManager.lock();
            isLocked.current = true;
        } else if (!isOpen && isLocked.current) {
            scrollLockManager.unlock();
            isLocked.current = false;
        }

        // Cleanup on unmount - ensure we unlock if we had locked
        return () => {
            if (isLocked.current) {
                scrollLockManager.unlock();
                isLocked.current = false;
            }
        };
    }, [isOpen]);
}

/**
 * Force reset scroll lock - use sparingly, e.g., on route change
 */
export function forceResetScrollLock() {
    scrollLockManager.forceUnlock();
}
